/**
 * The browser's own chrome around the app on a phone (device mode): the
 * status bar strip and the safe areas Safari tints, which take their color
 * from `theme-color`, the page background and (in recent Safari) the fixed
 * element at the top edge. The page's defaults follow the OS appearance, not
 * the app, so an app with its own bar color (Ice Cubes' themes) or its own
 * `.preferredColorScheme` sat under a strip of a different color.
 *
 * After each layout this samples the color the app draws at the middle of
 * its top edge (every layer there, translucent bars over what is behind
 * them) and gives it to `theme-color`, the page background and the device
 * element; the page's `color-scheme` follows the app's.
 */

/** An RGBA color, 0–255 channels and 0–1 alpha. */
interface Rgba {
  r: number;
  g: number;
  b: number;
  a: number;
}

/** `rgb(…)` / `rgba(…)` as computed styles write it, or `#rgb` / `#rrggbb`; null for anything else. */
export function parseCssColor(value: string): Rgba | null {
  const hex = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(value.trim());
  if (hex) {
    const digits = hex[1]!.length === 3 ? [...hex[1]!].map((d) => d + d).join('') : hex[1]!;
    const n = Number.parseInt(digits, 16);
    return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255, a: 1 };
  }
  const match = /^rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)(?:\s*[,/]\s*([\d.]+%?))?\s*\)$/.exec(value.trim());
  if (!match) return null;
  let a = 1;
  if (match[4] !== undefined) a = match[4].endsWith('%') ? Number.parseFloat(match[4]) / 100 : Number(match[4]);
  return { r: Number(match[1]), g: Number(match[2]), b: Number(match[3]), a };
}

/**
 * The color seen through `layers`, topmost first, over an opaque `base`
 * (source-over compositing; layers below an opaque one do not show).
 */
export function compositeColors(layers: readonly Rgba[], base: Rgba): Rgba {
  let r = 0;
  let g = 0;
  let b = 0;
  let a = 0;
  for (const layer of [...layers, { ...base, a: 1 }]) {
    const weight = (1 - a) * layer.a;
    r += layer.r * weight;
    g += layer.g * weight;
    b += layer.b * weight;
    a += weight;
    if (a >= 0.999) break;
  }
  return { r: r / a, g: g / a, b: b / a, a: 1 };
}

/** `#rrggbb`. */
export function hexColor(color: Rgba): string {
  const channel = (value: number): string =>
    Math.round(Math.min(255, Math.max(0, value)))
      .toString(16)
      .padStart(2, '0');
  return `#${channel(color.r)}${channel(color.g)}${channel(color.b)}`;
}

/** At most one sample in this many milliseconds. */
const MIN_INTERVAL = 250;

/** A painted background under the sampled point, and where it paints in the stack. */
interface Layer {
  color: Rgba;
  /** The explicit `z-index`es from the screen down to it. */
  z: number[];
  /** Its document order. */
  order: number;
}

/** Whether `a` paints above `b`: by stacking order, then later in the document. */
function above(a: Layer, b: Layer): number {
  for (let i = 0; i < Math.max(a.z.length, b.z.length); i++) {
    const d = (a.z[i] ?? 0) - (b.z[i] ?? 0);
    if (d !== 0) return -d;
  }
  return b.order - a.order;
}

/**
 * The backgrounds painted at (`x`, `y`) inside `root`, topmost first. Only
 * elements whose box holds the point are walked (the engine places children
 * inside their parents; a zero-size slot is walked through), so this reads
 * tens of boxes and styles, not the page. `elementsFromPoint` would skip the many app layers that let
 * pointers through (`pointer-events: none`), and turning that off restyles
 * every element.
 */
export function layersAt(root: Element, x: number, y: number): Rgba[] {
  const view = root.ownerDocument.defaultView;
  if (!view) return [];
  const layers: Layer[] = [];
  let order = 0;
  const walk = (parent: Element, opacity: number, z: number[]): void => {
    for (const el of parent.children) {
      const r = el.getBoundingClientRect();
      const inside = x >= r.left && x < r.right && y >= r.top && y < r.bottom;
      // A zero-size wrapper (a slot) is walked through: its children overflow it.
      if (!inside && r.width > 0 && r.height > 0) continue;
      const style = view.getComputedStyle(el);
      if (style.display === 'none') continue;
      const alpha = opacity * Number(style.opacity || 1);
      if (alpha <= 0.001) continue;
      const own = style.position !== 'static' && style.zIndex !== 'auto' ? [...z, Number(style.zIndex) || 0] : z;
      const color = inside && style.visibility === 'visible' ? parseCssColor(style.backgroundColor) : null;
      if (color && color.a > 0) layers.push({ color: { ...color, a: color.a * alpha }, z: own, order: order++ });
      walk(el, alpha, own);
    }
  };
  walk(root, 1, []);
  return layers.sort(above).map((layer) => layer.color);
}

/** Keeps the browser chrome's color in step with the app's top edge. */
export class ChromeColor {
  private scheduled = false;
  private last = 0;
  private applied = '';
  private readonly meta: HTMLMetaElement;

  constructor(
    private readonly device: HTMLElement,
    private readonly screen: HTMLElement,
  ) {
    const doc = device.ownerDocument;
    // One `theme-color` the page sets, in place of the per-appearance pair.
    for (const meta of doc.querySelectorAll('meta[name="theme-color"]')) meta.remove();
    this.meta = doc.createElement('meta');
    this.meta.name = 'theme-color';
    doc.head.append(this.meta);
    // A push, pop or sheet changes the top edge when its animation ends.
    screen.addEventListener('animationend', () => this.schedule());
    screen.addEventListener('transitionend', () => this.schedule());
  }

  /** Sample again once the current frame is drawn (call after each layout and theme change). */
  schedule(): void {
    if (this.scheduled) return;
    this.scheduled = true;
    const wait = Math.max(0, this.last + MIN_INTERVAL - performance.now());
    setTimeout(() => {
      requestAnimationFrame(() => {
        this.scheduled = false;
        this.last = performance.now();
        this.sample();
      });
    }, wait);
  }

  private sample(): void {
    const doc = this.device.ownerDocument;
    const rect = this.screen.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return;
    const view = doc.defaultView;
    if (!view) return;
    const layers = layersAt(this.screen, rect.left + rect.width / 2, rect.top + 1);
    const theme = this.device.dataset['theme'] === 'dark' ? 'dark' : 'light';
    const base =
      parseCssColor(view.getComputedStyle(this.device).getPropertyValue('--sb-color-system-background')) ??
      (theme === 'dark' ? { r: 0, g: 0, b: 0, a: 1 } : { r: 255, g: 255, b: 255, a: 1 });
    const color = hexColor(compositeColors(layers, base));
    const key = `${color}|${theme}`;
    if (key === this.applied) return;
    this.applied = key;
    this.meta.content = color;
    const root = doc.documentElement;
    root.style.colorScheme = theme;
    root.style.backgroundColor = color;
    doc.body.style.backgroundColor = color;
    this.device.style.backgroundColor = color;
  }
}
